import React, { useEffect, useState } from "react";
import { Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { ownerApi } from "@/lib/ownerApi";
import { MonthPicker, ScoreBadge, PartsList, lastMonths } from "@/pages/star/MyScore";

const inr = (n) => (n === null || n === undefined ? "—" : `${n < 0 ? "−" : ""}₹${Math.abs(Math.round(n)).toLocaleString("en-IN")}`);

const ScoreRow = ({ s }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-stone-100 last:border-0" data-testid={`team-score-${s.teacher_id}`}>
      <button className="w-full flex items-center gap-3 py-3 text-left" onClick={() => setOpen(!open)} data-testid={`team-score-toggle-${s.teacher_id}`}>
        <span className="w-8 text-center font-bold text-stone-500">#{s.rank}</span>
        <span className="flex-1 font-semibold text-stone-900">{s.name}<span className="ml-2 text-xs font-normal text-stone-500">{s.students} students</span></span>
        <ScoreBadge score={s.score} />
        {open ? <ChevronUp className="h-4 w-4 text-stone-400" /> : <ChevronDown className="h-4 w-4 text-stone-400" />}
      </button>
      {open && <div className="pb-3 pl-11 pr-2"><PartsList parts={s.parts} prefix={`team-${s.teacher_id}`} /></div>}
    </div>
  );
};

const MoneyTable = ({ money }) => (
  <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white" data-testid="team-money">
    <table className="w-full text-sm">
      <thead className="bg-stone-50 text-xs text-stone-600"><tr>
        {["Teacher", "Fees managed", "Collected", "Salary (year)", "Students", "Cost / student", "Profit contribution", "Margin", "Collection"].map((h) => <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>)}
      </tr></thead>
      <tbody>
        {money.teachers.map((t) => (
          <tr key={t.key} className="border-t border-stone-100" data-testid={`team-money-${t.key}`}>
            <td className="px-3 py-2"><div className="font-semibold">{t.name}</div><div className="text-[11px] text-stone-500">{t.basis}</div></td>
            <td className="px-3 py-2">{inr(t.fees_managed)}</td>
            <td className="px-3 py-2">{inr(t.collected)}</td>
            <td className="px-3 py-2">{inr(t.salary_year)}</td>
            <td className="px-3 py-2">{t.students}</td>
            <td className="px-3 py-2">{inr(t.cost_per_student)}</td>
            <td className={`px-3 py-2 font-semibold ${t.profit < 0 ? "text-red-700" : "text-emerald-800"}`}>{inr(t.profit)}</td>
            <td className="px-3 py-2">{t.margin_pct === null ? "—" : `${t.margin_pct}%`}</td>
            <td className="px-3 py-2">{t.collection_pct === null ? "—" : `${t.collection_pct}%`}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function TeamScores() {
  const [month, setMonth] = useState(lastMonths()[0]);
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); ownerApi.get("/team/scorecards", { params: { month } }).then((r) => setD(r.data)).catch(() => setD({ error: true })); }, [month]);
  return (
    <div className="space-y-6" data-testid="team-scores">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div><h2 className="font-heading text-xl font-extrabold text-stone-900">Teacher Scorecard</h2>
          <p className="text-sm text-stone-500">Monthly score out of 100 from attendance, punctuality, follow-ups, fees, stars, profiles, retention, admissions and replies. Teachers see only their own score and rank.</p></div>
        <MonthPicker value={month} onChange={setMonth} testid="team-score-month" />
      </div>
      {!d ? <Loader2 className="h-5 w-5 animate-spin text-emerald-800" /> : d.error ? <p className="text-sm text-red-600">Could not load scores.</p> : (
        <>
          <div className="rounded-xl border border-stone-200 bg-white px-4">{d.scores.map((s) => <ScoreRow key={s.teacher_id} s={s} />)}</div>
          <div className="space-y-2">
            <h3 className="font-heading text-lg font-bold text-stone-900">Money view — {d.money.ay}</h3>
            <p className="text-sm text-stone-500">Fees managed = full-year fees (from your confirmed Excel import plus new website admissions) of the groups in each teacher's salary rule, share-adjusted, or of their assigned students for fixed-salary teachers. Salary = full-year salary from Salary Settings, calculated on the same fees (matches Profit &amp; Forecast). Profit contribution = fees managed − salary. Sorted by margin: best value at top, most expensive at bottom.</p>
            <MoneyTable money={d.money} />
          </div>
        </>
      )}
    </div>
  );
}
